import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

interface TenantInfo {
  orgId: string
  opunitId: string
  orgLabel?: string
  opunitLabel?: string
}

interface TenantState {
  active: TenantInfo | null
  allowed: TenantInfo[]
}

const initialState: TenantState = {
  active: null,
  allowed: [],
}

const TenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    // Set the active tenant
    setActiveTenant: (state, action: PayloadAction<TenantInfo>) => {
      state.active = action.payload
    },

    // Set the allowed tenants
    setAllowedTenants: (state, action: PayloadAction<TenantInfo[]>) => {
      state.allowed = action.payload
    },

    // Set both active and allowed tenants
    setTenantDetails: (
      state,
      action: PayloadAction<{ active: TenantInfo; allowed: TenantInfo[] }>
    ) => {
      state.active = action.payload.active
      state.allowed = action.payload.allowed
    },

    // Add a tenant to allowed list
    addAllowedTenant: (state, action: PayloadAction<TenantInfo>) => {
      const exists = state.allowed.some(
        (tenant) =>
          tenant.orgId === action.payload.orgId &&
          tenant.opunitId === action.payload.opunitId
      )
      if (!exists) {
        state.allowed.push(action.payload)
      }
    },

    // Remove a tenant from allowed list
    removeAllowedTenant: (
      state,
      action: PayloadAction<{ orgId: string; opunitId: string }>
    ) => {
      state.allowed = state.allowed.filter(
        (tenant) =>
          !(
            tenant.orgId === action.payload.orgId &&
            tenant.opunitId === action.payload.opunitId
          )
      )
    },

    // Clear all tenant data
    clearTenantDetails: (state) => {
      state.active = null
      state.allowed = []
    },

    // Update active tenant labels
    updateActiveTenantLabels: (
      state,
      action: PayloadAction<{ orgLabel?: string; opunitLabel?: string }>
    ) => {
      if (state.active) {
        if (action.payload.orgLabel !== undefined) {
          state.active.orgLabel = action.payload.orgLabel
        }
        if (action.payload.opunitLabel !== undefined) {
          state.active.opunitLabel = action.payload.opunitLabel
        }
      }
    },
  },
})

// Selectors
export const selectActiveTenant = (state: { tenant: TenantState }) =>
  state?.tenant?.active

export const selectAllowedTenants = (state: { tenant: TenantState }) =>
  state?.tenant?.allowed

export const selectActiveOrgId = (state: { tenant: TenantState }) =>
  state?.tenant?.active?.orgId

export const selectActiveOpunitId = (state: { tenant: TenantState }) =>
  state?.tenant?.active?.opunitId

export const selectActiveOrgLabel = (state: { tenant: TenantState }) =>
  state?.tenant?.active?.orgLabel

export const selectActiveOpunitLabel = (state: { tenant: TenantState }) =>
  state?.tenant?.active?.opunitLabel

export const selectTenantDetails = (state: { tenant: TenantState }) =>
  state?.tenant

export const {
  setActiveTenant,
  setAllowedTenants,
  setTenantDetails,
  addAllowedTenant,
  removeAllowedTenant,
  clearTenantDetails,
  updateActiveTenantLabels,
} = TenantSlice.actions

export default TenantSlice.reducer
